import { useState } from 'react';
import { CalendarIcon, X } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import dayjs from 'dayjs';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DateRangePickerProps {
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
  className?: string;
}

function formatDate(date: Date): string {
  return dayjs(date).format('YYYY-MM-DD');
}

export function DateRangePicker({ value, onChange, className }: DateRangePickerProps) {
  const [draft, setDraft] = useState<DateRange | undefined>();

  const label =
    value?.from || value?.to
      ? `${value.from ? formatDate(value.from) : '…'} → ${value.to ? formatDate(value.to) : '…'}`
      : null;

  return (
    <Popover
      onOpenChange={(open) => {
        if (!open) setDraft(undefined);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'justify-start text-left font-normal',
            !label && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="size-4" />
          {label ?? '—'}
          {label && (
            <span
              role="button"
              aria-label="Clear"
              className="hover:bg-accent -mr-1 ml-1 rounded p-0.5"
              onClick={(e) => {
                e.stopPropagation();
                setDraft(undefined);
                onChange(undefined);
              }}
            >
              <X className="size-3" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={draft ?? value}
          onSelect={(range) => {
            setDraft(range);
            if (!range || (range.from && range.to)) {
              onChange(range);
              setDraft(undefined);
            }
          }}
          numberOfMonths={2}
          defaultMonth={value?.from}
        />
      </PopoverContent>
    </Popover>
  );
}
